/**
 * Scroll one section into view inside a scroll container that isn't the
 * document.
 *
 * `scrollIntoView` is a no-op here and it is worth saying why: the landing page
 * is `position:fixed; inset:0` with its own `overflow-y`, so the *document*
 * never scrolls. The browser's own implementation walks up looking for a
 * scrollable ancestor and finds one, but it also scrolls every other scrollable
 * ancestor on the way — including the fixed container itself, which cannot
 * move — and lands somewhere approximate. So this is hand-rolled.
 *
 * 420ms on a cubic ease-out, which is the redesign's anchored-scroll row: long
 * enough that the page reads as having travelled rather than jumped, short
 * enough that nobody waits for it.
 */

const DURATION_MS = 420

/** Cubic ease-out: fast away, gentle arrival. */
function ease(t: number): number {
  return 1 - (1 - t) ** 3
}

let running: number | null = null

export function scrollWithin(containerId: string, targetId: string) {
  const container = document.getElementById(containerId)
  const target = document.getElementById(targetId)
  if (!container || !target) return

  // Clamped to the container's own bounds, or the last few pixels of the
  // animation run against a scrollTop that has already stopped moving and the
  // easing appears to stall at the bottom of the page.
  const max = container.scrollHeight - container.clientHeight
  const from = container.scrollTop
  const to = Math.max(0, Math.min(max, from + target.getBoundingClientRect().top - headerHeight()))
  if (to === from) return

  if (running !== null) cancelAnimationFrame(running)

  const started = performance.now()

  function step(now: number) {
    const t = Math.min(1, (now - started) / DURATION_MS)
    container!.scrollTop = from + (to - from) * ease(t)
    running = t < 1 ? requestAnimationFrame(step) : null
  }

  running = requestAnimationFrame(step)
}

/**
 * The sticky header sits over the top of the container, so a section scrolled
 * to its exact top would arrive underneath it. Measured rather than guessed —
 * the header is 62px today and this stops that being written down twice.
 */
function headerHeight(): number {
  const header = document.querySelector<HTMLElement>('#landing-scroll > header')
  return header ? header.getBoundingClientRect().height : 0
}

/**
 * Honour a `#section` in the URL on first paint, so a link from a mailer — or
 * the redirect the waitlist form comes back on — lands in the right place.
 * Instant rather than animated: this is arriving, not travelling.
 */
export function jumpToHash(containerId: string) {
  const id = window.location.hash.slice(1)
  if (!id) return

  const container = document.getElementById(containerId)
  const target = document.getElementById(id)
  if (!container || !target) return

  container.scrollTop = Math.max(
    0,
    container.scrollTop + target.getBoundingClientRect().top - headerHeight()
  )
}

/**
 * Wire the landing page's own anchors up, and honour any fragment it arrived
 * with. Delegated from the document rather than bound per link, so it survives
 * a Turbo render with nothing to re-bind.
 */
export function initLandingAnchors() {
  function jump() {
    if (document.getElementById('landing-scroll')) jumpToHash('landing-scroll')
  }

  document.addEventListener('click', (event) => {
    const anchor = (event.target as HTMLElement)?.closest?.<HTMLElement>('[data-landing-anchor]')
    if (!anchor) return

    // Only once we know we can do better than the browser would. Without the
    // container the default jump is the right answer and this gets out of the
    // way.
    if (!document.getElementById('landing-scroll')) return

    event.preventDefault()
    scrollWithin('landing-scroll', anchor.dataset.landingAnchor ?? '')
  })

  jump()
  document.addEventListener('turbo:render', jump)
}
